import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import type { CrewHopDirection } from "../lib/crewContinuity";
import { adjacentCrewId, browserSessionStorage, crewSwipeDirection, lastCrewSurface } from "../lib/crewContinuity";
import { formatAge } from "../lib/time";
import { crewIdFromPathname, crewSurfaceFromPathname, crewSurfacePath } from "../lib/routes";
import { useRemote } from "../state/RemoteContext";
import { useCrewContinuity } from "../state/useCrewContinuity";
import { Brand, Icon, type IconName } from "./Icon";
import { ProjectionBanner, StatusCluster } from "./StatusCluster";

interface NavItem {
  label: string;
  to: string;
  icon: IconName;
}

const globalNavigation: NavItem[] = [
  { label: "Attention", to: "/attention", icon: "activity" },
  { label: "Crews", to: "/crews", icon: "crew" },
  { label: "Session", to: "/session", icon: "link" },
];

export function AppShell() {
  const { fixture, dialog, setDialog, sourceKind } = useRemote();
  const location = useLocation();
  const navigate = useNavigate();
  const detailPaneRef = useRef<HTMLElement>(null);
  const pointerStart = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const suppressNextClick = useRef(false);
  const [hopDirection, setHopDirection] = useState<CrewHopDirection | null>(null);
  const crewId = crewIdFromPathname(location.pathname);
  const crew = fixture.crews.find((candidate) => candidate.id === crewId);
  const activeCrewSurface = crewSurfaceFromPathname(location.pathname) ?? "pulse";
  const crewIds = fixture.crews.map((candidate) => candidate.id);
  const crewIndex = crew ? crewIds.indexOf(crew.id) : -1;
  const previousCrewId = crew ? adjacentCrewId(crewIds, crew.id, -1) : null;
  const nextCrewId = crew ? adjacentCrewId(crewIds, crew.id, 1) : null;
  const previousCrew = fixture.crews.find((candidate) => candidate.id === previousCrewId);
  const nextCrew = fixture.crews.find((candidate) => candidate.id === nextCrewId);
  const canHopCrews = fixture.crews.length > 1;
  const crewNavigation: NavItem[] = crew
    ? [
        { label: "Pulse", to: crewSurfacePath(crew.id, "pulse"), icon: "activity" },
        { label: "Manager", to: crewSurfacePath(crew.id, "manager"), icon: "chat" },
        { label: "Board", to: crewSurfacePath(crew.id, "board"), icon: "board" },
      ]
    : globalNavigation;

  useCrewContinuity(fixture.session.sessionId, location.pathname, detailPaneRef);

  useEffect(() => {
    if (!hopDirection) return;
    const timer = window.setTimeout(() => setHopDirection(null), 220);
    return () => window.clearTimeout(timer);
  }, [hopDirection, location.pathname]);

  const navigateToRememberedCrew = (targetCrewId: string) => {
    const surface = lastCrewSurface(browserSessionStorage(), fixture.session.sessionId, targetCrewId);
    navigate(crewSurfacePath(targetCrewId, surface));
  };

  const hopCrew = (direction: CrewHopDirection) => {
    if (!crew) return;
    const targetCrewId = adjacentCrewId(crewIds, crew.id, direction);
    if (!targetCrewId || targetCrewId === crew.id) return;
    setHopDirection(direction);
    navigate(crewSurfacePath(targetCrewId, activeCrewSurface));
  };

  const beginCrewSwipe = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!canHopCrews || !event.isPrimary || event.button !== 0) return;
    pointerStart.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
  };

  const finishCrewSwipe = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start || start.pointerId !== event.pointerId) return;
    const direction = crewSwipeDirection(start, { x: event.clientX, y: event.clientY });
    if (!direction) return;
    event.preventDefault();
    suppressNextClick.current = true;
    window.setTimeout(() => { suppressNextClick.current = false; }, 0);
    hopCrew(direction);
  };

  const suppressSwipeClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!suppressNextClick.current) return;
    suppressNextClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className="app-shell">
      <header className={`app-header${crew ? " app-header--crew" : ""}`}>
        {crew ? (
          <>
            <button type="button" className="icon-button mobile-only crew-back-button" onClick={() => navigate("/attention")} aria-label="Back to all Crews">
              <Icon name="chevron" />
            </button>
            <div
              className="crew-navigator"
              aria-label="Crew navigation"
              onPointerDown={beginCrewSwipe}
              onPointerUp={finishCrewSwipe}
              onPointerCancel={() => { pointerStart.current = null; }}
              onClickCapture={suppressSwipeClick}
            >
              <button
                type="button"
                className="crew-hop-button crew-hop-button--previous"
                onClick={() => hopCrew(-1)}
                disabled={!canHopCrews}
                aria-label={`Previous Crew${previousCrew ? `, ${previousCrew.name}` : ""}`}
              >
                <Icon name="chevron" size={17} />
              </button>
              <button
                type="button"
                className="crew-heading"
                onClick={() => setDialog({ type: "crew-switcher", crewId: crew.id })}
                aria-haspopup="dialog"
                aria-expanded={dialog?.type === "crew-switcher"}
              >
                <span className="eyebrow">Crew {crewIndex + 1}/{fixture.crews.length} <span aria-hidden="true">▾</span></span>
                <strong>{crew.name}</strong>
              </button>
              <button
                type="button"
                className="crew-hop-button crew-hop-button--next"
                onClick={() => hopCrew(1)}
                disabled={!canHopCrews}
                aria-label={`Next Crew${nextCrew ? `, ${nextCrew.name}` : ""}`}
              >
                <Icon name="chevron" size={17} />
              </button>
              <div className="crew-position" role="status" aria-live="polite">
                <span className="visually-hidden">Crew {crewIndex + 1} of {fixture.crews.length}</span>
                <span className="crew-position__dots" aria-hidden="true">
                  {fixture.crews.map((candidate, index) => (
                    <span key={candidate.id} className={index === crewIndex ? "is-current" : ""} />
                  ))}
                </span>
              </div>
            </div>
            <nav className="crew-surface-nav" aria-label={`${crew.name} surfaces`}>
              {crewNavigation.map((item) => (
                <NavLink key={item.to} to={item.to} className={({ isActive }) => `crew-surface-nav__item${isActive ? " is-active" : ""}`}>
                  <Icon name={item.icon} /><span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </>
        ) : <Brand />}
        {sourceKind === "development-fixture" && <span className="development-source-badge">Dev fixture</span>}
        <StatusCluster session={fixture.session} compact />
      </header>
      <ProjectionBanner session={fixture.session} />

      <div className="workspace">
        <aside className="master-rail" aria-label="Forge Remote navigation">
          <div className="master-brand"><Brand /></div>
          <nav className="master-nav" aria-label="Global">
            {globalNavigation.map((item) => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => `master-nav__item${isActive ? " is-active" : ""}`}>
                <Icon name={item.icon} /><span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="master-section-heading">
            <span>Needs you</span>
            <strong>{fixture.attention.filter((item) => ["decision", "risk", "blocker", "command-failed"].includes(item.kind)).length}</strong>
          </div>
          <div className="master-attention">
            {fixture.attention.slice(0, 3).map((item) => {
              const itemCrew = fixture.crews.find((candidate) => candidate.id === item.crewId);
              return (
                <button type="button" className="master-attention__item" data-kind={item.kind} key={item.id} onClick={() => navigate(crewSurfacePath(item.crewId))}>
                  <span>{itemCrew?.shortName} · {item.kind}</span>
                  <strong>{item.title}</strong>
                  <small>{formatAge(item.occurredAt)} ago</small>
                </button>
              );
            })}
          </div>
          <div className="master-section-heading"><span>Crews</span><strong>{fixture.crews.length}</strong></div>
          <div className="master-crews">
            {fixture.crews.map((item) => (
              <button type="button" key={item.id} className={item.id === crew?.id ? "is-active" : ""} onClick={() => navigateToRememberedCrew(item.id)}>
                <span className="health-dot" data-health={item.health} aria-hidden="true" />
                <span><strong>{item.name}</strong><small>{item.runState} · {formatAge(item.members[0].lastBeatAt)}</small></span>
              </button>
            ))}
          </div>
        </aside>
        <main
          className="detail-pane"
          id="main-content"
          ref={detailPaneRef}
          data-crew-hop={hopDirection === 1 ? "next" : hopDirection === -1 ? "previous" : undefined}
        >
          <Outlet />
        </main>
      </div>

      <nav className="bottom-nav" aria-label={crew ? `${crew.name} surfaces` : "Forge Remote"}>
        {crewNavigation.map((item) => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => `bottom-nav__item${isActive ? " is-active" : ""}`}>
            <Icon name={item.icon} /><span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
